import 'whatwg-fetch'; // Polyfill for fetch for older IE / Safari versions
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { StylesProvider } from '@material-ui/styles';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';
import { merge } from 'lodash';

import WDLTheme from 'LegacyWeployDesignLanguage/Theme';
import dagstorp from 'LegacyWeployDesignLanguage/dagstorp';

import { weployerDetailsStorage } from '../../utils/storage';
import GraphQLProvider from '../../containers/graphQL/Provider';
import Login from './login/Login';
import Dashboard from './dashboard/Dashboard';
import Timesheets from './timesheets/Timesheets';
import Jobs from './jobs/Jobs';
import JobTemplates from './jobs/templates';
import Search from './search/Search';

import JobDetails from './jobDetails/JobDetails';
import GlobalStyles from './GlobalStyles';
import { PolicyProvider } from './PolicyProvider';
import Users from './users/Users';
import AuthRoute from './AuthRoute';
import RouteChangeListener from './RouteChangeListener';
import Unauthorized from './errors/Unauthorized';
import { appCache } from '../../clients/graphql';
import { navigateExternal } from './common/navigation';

import {
  DASHBOARD_PATH,
  getExternalPath,
  getInternalPath,
  JOB_DETAILS_PATH,
  JOBS_PATH,
  JOBS_TEMPLATES_PATH,
  LOGIN_PATH,
  SERVER_REDIRECT_PATHS,
  USERS_PATH,
  TIMESHEETS_PATH,
  UNAUTHORIZED_PATH,
  SEARCH_PATH,
} from './pathHelpers';

import { ME, OWNED_BY_FILTER_KEY } from './common/graphql/constants';
import { identify } from '../../utils/analytics';

const DEFAULT_WEPLOYER_DETAILS = { [OWNED_BY_FILTER_KEY]: ME, userId: null, organisationId: null };

const NoMatch = () => <h2>Page not found</h2>;

const theme = merge(WDLTheme, dagstorp);

const App = (props) => {
  const weployerDetailsFromLocalStorage = weployerDetailsStorage.getItem();
  const [weployerDetails, setWeployerDetails] = useState(Object.assign(DEFAULT_WEPLOYER_DETAILS, weployerDetailsFromLocalStorage));
  const [weployerRole, setWeployerRole] = useState(null);

  useEffect(() => {
    const { userId, organisationId } = weployerDetails;

    if (userId && organisationId) {
      identify(userId, { organisationId });
    }
  }, [weployerDetails]);

  const handleWeployerDetailsChange = (key, value) => {
    const updatedDetails = { ...weployerDetails, [key]: value };
    weployerDetailsStorage.setItem(updatedDetails);
    setWeployerDetails(updatedDetails);
  };

  const beforeRedirect = () => {
    weployerDetailsStorage.removeItem();

    appCache.writeData({
      data: {
        authStatus: {
          __typename: 'authStatus',
          status: null,
        },
      },
    });
  };

  const handleInitialPolicyFetchSuccess = (policyObject) => {
    if (!weployerRole) {
      const { role } = policyObject;
      setWeployerRole(role);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <StylesProvider injectFirst>
        <GraphQLProvider>
          <Router>
            <RouteChangeListener>
              <AuthRoute
                redirectPathOnLoggedOut={getExternalPath({ pathName: LOGIN_PATH })}
                unauthorizedPagePath={getInternalPath({ pathName: UNAUTHORIZED_PATH })}
                beforeRedirect={beforeRedirect}
              >
                <Switch>
                  <Redirect from={SERVER_REDIRECT_PATHS} to={getInternalPath({ pathName: JOBS_PATH })} />
                  <Route
                    exact
                    path={getExternalPath({ pathName: LOGIN_PATH })}
                    render={routeProps => <Login {...routeProps} {...props} />}
                  />
                  <Route
                    exact
                    path={getInternalPath({ pathName: DASHBOARD_PATH })}
                    render={routeProps => (
                      <PolicyProvider onFetchSuccess={handleInitialPolicyFetchSuccess}>
                        <Dashboard
                          {...routeProps}
                          {...props}
                          timesheetsPath={getInternalPath({ pathName: TIMESHEETS_PATH })}
                          jobsPath={getInternalPath({ pathName: JOBS_PATH })}
                          onWeployerDetailsChange={handleWeployerDetailsChange}
                          weployerDetails={weployerDetails}
                          navigateExternal={navigateExternal}
                        />
                      </PolicyProvider>
                    )}
                  />
                  <Route
                    exact
                    path={getInternalPath({ pathName: JOBS_PATH })}
                    render={routeProps => (
                      <PolicyProvider>
                        <Jobs
                          {...routeProps}
                          {...props}
                          onWeployerDetailsChange={handleWeployerDetailsChange}
                          weployerDetails={weployerDetails}
                          navigateExternal={navigateExternal}
                        />
                      </PolicyProvider>
                    )}
                  />
                  <Route
                    exact
                    path={getInternalPath({ pathName: JOBS_TEMPLATES_PATH })}
                    render={routeProps => (
                      <PolicyProvider>
                        <JobTemplates
                          {...routeProps}
                          {...props}
                          navigateExternal={navigateExternal}
                        />
                      </PolicyProvider>
                    )}
                  />
                  <Route
                    exact
                    path={getInternalPath({ pathName: JOB_DETAILS_PATH })}
                    render={routeProps => (
                      <PolicyProvider>
                        <JobDetails
                          {...routeProps}
                          {...props}
                          navigateExternal={navigateExternal}
                        />
                      </PolicyProvider>
                    )}
                  />
                  <Route
                    exact
                    path={getInternalPath({ pathName: TIMESHEETS_PATH })}
                    render={routeProps => (
                      <PolicyProvider>
                        <Timesheets
                          {...routeProps}
                          {...props}
                          onWeployerDetailsChange={handleWeployerDetailsChange}
                          weployerDetails={weployerDetails}
                        />
                      </PolicyProvider>
                    )}
                  />
                  <Route
                    exact
                    path={getInternalPath({ pathName: USERS_PATH })}
                    render={routeProps => (
                      <PolicyProvider>
                        <Users
                          {...routeProps}
                          {...props}
                          navigateExternal={navigateExternal}
                        />
                      </PolicyProvider>
                    )}
                  />
                  <Route exact path={getInternalPath({ pathName: SEARCH_PATH })}>
                    <PolicyProvider>
                      <Search />
                    </PolicyProvider>
                  </Route>
                  <Route
                    exact
                    path={getInternalPath({ pathName: UNAUTHORIZED_PATH })}
                    render={routeProps => (
                      <Unauthorized {...routeProps} />
                    )}
                  />
                  <Route component={NoMatch} />
                </Switch>
              </AuthRoute>
            </RouteChangeListener>
          </Router>
        </GraphQLProvider>
      </StylesProvider>
    </ThemeProvider>
  );
};

App.propTypes = {
  applicationConfig: PropTypes.shape({
    railsPaths: PropTypes.object,
    features: PropTypes.object,
  }),
};

App.defaultProps = {
  applicationConfig: {},
};

export default App;
